import { Component, Input } from '@angular/core';
import { AlertService } from '@services/helpers/alert/alert.service';
import { Alert, AlertType } from '@models/app/alert/Alert';
import { animate, style, transition, trigger } from '@angular/animations';
import { IconComponent } from '../icon/icon.component';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  animations: [
    trigger('fade', [
      transition('void => *', [
        style({ opacity: 0, transform: 'translateY(-6px) scale(0.99)' }),
        animate('160ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition('* => void', [
        animate('120ms ease-in', style({ opacity: 0, transform: 'translateY(-4px) scale(0.99)' })),
      ]),
    ]),
  ],
  standalone: true,
  imports: [IconComponent, AsyncPipe, NgClass, NgStyle],
})
export class AlertComponent {
  @Input() fade = true;
  AlertType = AlertType;

  constructor(public alertService: AlertService) {}

  removeAlert(alert: Alert): void {
    this.alertService.removeAlert(alert);
  }

  alertIconPillClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'bg-emerald-500/15 text-emerald-400 ring-1 ring-inset ring-emerald-500/30';
      case AlertType.Error:
        return 'bg-rose-500/15 text-rose-400 ring-1 ring-inset ring-rose-500/30';
      case AlertType.Warning:
        return 'bg-amber-500/15 text-amber-400 ring-1 ring-inset ring-amber-500/30';
      case AlertType.Info:
        return 'bg-sky-500/15 text-sky-400 ring-1 ring-inset ring-sky-500/30';
      case AlertType.Live:
        return 'bg-cyan-500/15 text-cyan-400 ring-1 ring-inset ring-cyan-500/30 animate-pulse';
      default:
        return 'bg-slate-500/15 text-slate-300 ring-1 ring-inset ring-slate-500/25';
    }
  }

  alertAccentGradient(alert: Alert): string {
    const accentColors: Record<AlertType, string> = {
      [AlertType.Success]: 'rgb(16 185 129)',
      [AlertType.Error]: 'rgb(244 63 94)',
      [AlertType.Info]: 'rgb(56 189 248)',
      [AlertType.Warning]: 'rgb(245 158 11)',
      [AlertType.Live]: 'rgb(34 211 238)',
    };
    const color = accentColors[alert?.type] || 'rgb(148 163 184)';
    return `linear-gradient(90deg, ${color} 0%, color-mix(in srgb, ${color} 40%, transparent) 40%, transparent 80%)`;
  }

  alertIconBadgeClass(alert: Alert): string {
    return this.alertIconPillClass(alert);
  }

  alertCardClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'border-emerald-500/35 hover:border-emerald-500/50';
      case AlertType.Error:
        return 'border-rose-500/35 hover:border-rose-500/50';
      case AlertType.Warning:
        return 'border-amber-500/35 hover:border-amber-500/50';
      case AlertType.Info:
        return 'border-sky-500/35 hover:border-sky-500/50';
      case AlertType.Live:
        return 'border-cyan-500/35 hover:border-cyan-500/50';
      default:
        return 'border-slate-700/80 hover:border-slate-600';
    }
  }

  cssClass(alert: Alert): string {
    if (!alert) {
      return '';
    }

    const classes = ['alert-shell'];

    const alertTypeClass = {
      [AlertType.Success]: 'alert-success',
      [AlertType.Error]: 'alert-danger',
      [AlertType.Info]: 'alert-info',
      [AlertType.Warning]: 'alert-warning',
      [AlertType.Live]: 'alert-live',
    };

    classes.push(alertTypeClass[alert.type]);

    if (alert.fade) {
      classes.push('fade');
    }

    return classes.join(' ');
  }

  alertStyle(alert: Alert): Record<string, string> {
    const palette = {
      [AlertType.Success]: {
        accent: 'rgb(16 185 129)',
        accentGlow: 'rgba(16, 185, 129, 0.45)',
        accentForeground: 'rgb(209 250 229)',
      },
      [AlertType.Error]: {
        accent: 'rgb(248 113 113)',
        accentGlow: 'rgba(248, 113, 113, 0.45)',
        accentForeground: 'rgb(255 228 230)',
      },
      [AlertType.Info]: {
        accent: 'rgb(59 130 246)',
        accentGlow: 'rgba(59, 130, 246, 0.45)',
        accentForeground: 'rgb(219 234 254)',
      },
      [AlertType.Warning]: {
        accent: 'rgb(245 158 11)',
        accentGlow: 'rgba(245, 158, 11, 0.45)',
        accentForeground: 'rgb(254 243 199)',
      },
      [AlertType.Live]: {
        accent: 'rgb(34 211 238)',
        accentGlow: 'rgba(34, 211, 238, 0.45)',
        accentForeground: 'rgb(207 250 254)',
      },
    }[alert?.type];

    return {
      '--alert-accent': palette?.accent || 'rgb(148 163 184)',
      '--alert-accent-glow': palette?.accentGlow || 'rgba(148 163 184, 0.45)',
      '--alert-accent-foreground': palette?.accentForeground || 'rgb(241 245 249)',
    };
  }

  alertTypeClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'border-emerald-400/35';
      case AlertType.Error:
        return 'border-red-400/35';
      case AlertType.Info:
        return 'border-blue-400/35';
      case AlertType.Warning:
        return 'border-amber-400/35';
      case AlertType.Live:
        return 'border-cyan-400/35';
      default:
        return 'border-white/10';
    }
  }

  alertLabel(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'Success';
      case AlertType.Error:
        return 'Error';
      case AlertType.Info:
        return 'Info';
      case AlertType.Warning:
        return 'Warning';
      case AlertType.Live:
        return 'Live';
      default:
        return 'Alert';
    }
  }

  alertIconClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'text-emerald-100';
      case AlertType.Error:
        return 'text-red-100';
      case AlertType.Info:
        return 'text-blue-100';
      case AlertType.Warning:
        return 'text-amber-100';
      case AlertType.Live:
        return 'text-cyan-100';
      default:
        return 'text-slate-100';
    }
  }
}
